import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ChevronRight, RotateCcw, Save, AlertCircle, CheckCircle, Search, X, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: 'Admin', management: 'Management', hr: 'HR', dgm: 'DGM',
  senior_approver: 'Sr. Approver', accounts: 'Accounts', bdm: 'BDM'
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  management: 'bg-blue-100 text-blue-700',
  hr: 'bg-yellow-100 text-yellow-700',
  dgm: 'bg-violet-100 text-violet-700',
  senior_approver: 'bg-orange-100 text-orange-700',
  accounts: 'bg-teal-100 text-teal-700',
  bdm: 'bg-gray-100 text-gray-700'
};

const PermissionsPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [definitions, setDefinitions] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPerms, setUserPerms] = useState(null);
  const [editPerms, setEditPerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, defsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/permissions/definitions`)
      ]);
      setUsers(usersRes.data);
      setDefinitions(defsRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectUser = async (u) => {
    setSelectedUser(u);
    setMessage(null);
    try {
      const res = await axios.get(`${API}/permissions/user/${u.id}`);
      setUserPerms(res.data);
      setEditPerms({
        modules: { ...res.data.modules },
        pages: { ...res.data.pages },
        actions: { ...res.data.actions }
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load user permissions' });
    }
  };

  const togglePerm = (group, key) => {
    setEditPerms(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: !prev[group][key] }
    }));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.put(`${API}/permissions/user/${selectedUser.id}/update`, editPerms);
      setUserPerms(res.data);
      setEditPerms({ modules: { ...res.data.modules }, pages: { ...res.data.pages }, actions: { ...res.data.actions } });
      setMessage({ type: 'success', text: `Permissions saved for ${selectedUser.name}` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.delete(`${API}/permissions/user/${selectedUser.id}`);
      setUserPerms(res.data);
      setEditPerms({ modules: { ...res.data.modules }, pages: { ...res.data.pages }, actions: { ...res.data.actions } });
      setMessage({ type: 'success', text: `Reset to ${ROLE_LABELS[selectedUser.role] || selectedUser.role} defaults` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reset' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!userPerms || !editPerms) return false;
    return JSON.stringify(userPerms.modules) !== JSON.stringify(editPerms.modules) ||
           JSON.stringify(userPerms.pages) !== JSON.stringify(editPerms.pages) ||
           JSON.stringify(userPerms.actions) !== JSON.stringify(editPerms.actions);
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="text-gray-600">Admin access required</p></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Shield className="w-5 h-5 text-indigo-500" /> Permissions Manager
        </h1>
        <p className="text-sm text-gray-500 mt-1">Control page access and action capabilities for each user</p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'error' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="permissions-message">
          {message.type === 'error' ? <AlertCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-green-500" />}
          <span className={`text-sm ${message.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{message.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: User List */}
        <div className="lg:col-span-1">
          <div className="card p-0 overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  className="input-field pl-9 text-sm"
                  data-testid="permissions-search"
                />
              </div>
            </div>
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
              {filteredUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => selectUser(u)}
                  className={`w-full px-4 py-3 flex items-center gap-3 text-left border-b border-gray-50 transition-colors hover:bg-gray-50 ${selectedUser?.id === u.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''}`}
                  data-testid={`perm-user-${u.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
                    {u.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{u.name}</div>
                    <div className="text-xs text-gray-400 truncate">{u.email}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase flex-shrink-0 ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              ))}
              {filteredUsers.length === 0 && (
                <div className="p-6 text-center text-sm text-gray-400">No users match your search</div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Permission Editor */}
        <div className="lg:col-span-2">
          {!selectedUser ? (
            <div className="card flex flex-col items-center justify-center py-16 text-center">
              <Shield className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">Select a user from the list to manage their permissions</p>
            </div>
          ) : !editPerms ? (
            <div className="card flex items-center justify-center py-16">
              <div className="text-gray-500">Loading permissions...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* User Header */}
              <div className="card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600">
                      {selectedUser.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{selectedUser.name}</div>
                      <div className="text-xs text-gray-400">{selectedUser.email}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${ROLE_COLORS[selectedUser.role] || 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                    </span>
                    {userPerms?.is_custom && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium border border-amber-200">Custom</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleReset} disabled={saving}
                      className="btn btn-secondary text-xs flex items-center gap-1.5" data-testid="reset-permissions-btn">
                      <RotateCcw className="w-3.5 h-3.5" /> Reset to Defaults
                    </button>
                    <button onClick={handleSave} disabled={saving || !hasChanges()}
                      className="btn btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40" data-testid="save-permissions-btn">
                      <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Module Access */}
              <div className="card" data-testid="module-permissions-card">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Module Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {definitions?.module_keys?.map(key => (
                    <button
                      key={key}
                      onClick={() => togglePerm('modules', key)}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all text-sm ${editPerms.modules?.[key] ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                      data-testid={`perm-module-${key}`}
                    >
                      <span className="font-medium truncate">{definitions.module_labels?.[key] || key}</span>
                      {editPerms.modules?.[key] ? (
                        <ToggleRight className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-300 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Page Permissions */}
              <div className="card" data-testid="page-permissions-card">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Page Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {definitions?.page_keys?.map(key => (
                    <button
                      key={key}
                      onClick={() => togglePerm('pages', key)}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all text-sm ${editPerms.pages[key] ? 'bg-indigo-50 border-indigo-200 text-indigo-800' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                      data-testid={`perm-page-${key}`}
                    >
                      <span className="font-medium truncate">{definitions.page_labels[key] || key}</span>
                      {editPerms.pages[key] ? (
                        <ToggleRight className="w-5 h-5 text-indigo-500 flex-shrink-0 ml-2" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-300 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Permissions */}
              <div className="card" data-testid="action-permissions-card">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Action Capabilities</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {definitions?.action_keys?.map(key => (
                    <button
                      key={key}
                      onClick={() => togglePerm('actions', key)}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all text-sm ${editPerms.actions[key] ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                      data-testid={`perm-action-${key}`}
                    >
                      <span className="font-medium truncate">{definitions.action_labels[key] || key}</span>
                      {editPerms.actions[key] ? (
                        <ToggleRight className="w-5 h-5 text-emerald-500 flex-shrink-0 ml-2" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-300 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
